import { IconSettings } from '@tabler/icons-react'
import { Link, useLocation } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/**
 * The shared look of the two square controls at the foot of the rail — the
 * Settings button and the account avatar. One square, one hover, one focus glow.
 */
export const railControlClass = cn(
  'glow-on-focus flex size-9 items-center justify-center rounded-lg text-muted-foreground outline-none',
  // box-shadow and transform ride the transition so the focus halo fades and
  // the press eases — the host owns the timing.
  'transition-[color,background-color,box-shadow,transform] duration-[var(--motion-interaction)] ease-out',
  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
  'active:translate-y-px active:scale-[0.98] active:duration-[var(--motion-press)]',
  'motion-reduce:transition-none motion-reduce:active:translate-y-0 motion-reduce:active:scale-100',
)

/**
 * Settings' one door in the chrome: a gear directly above the account avatar
 * (at the rail's foot, and in the phone sheet's footer where there is no
 * rail). It opens Settings' first page and stays lit on every /settings page.
 * The Settings section still owns those pages in lib/nav.ts — that is what
 * gives the inner menu its Settings entries — it is just not a rail tile.
 */
export function RailSettings({
  onSelect,
  side = 'right',
  className,
}: {
  onSelect?: () => void
  side?: 'right' | 'top'
  className?: string
}) {
  const { pathname } = useLocation()
  const active = pathname === '/settings' || pathname.startsWith('/settings/')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/settings/accounts"
          onClick={onSelect}
          aria-label="Settings"
          aria-current={active ? 'page' : undefined}
          className={cn(
            railControlClass,
            active && 'bg-foreground/[0.08] text-foreground shadow-xs ring-1 ring-sidebar-border',
            className,
          )}
        >
          <IconSettings className="size-[18px]" stroke={active ? 2 : 1.8} aria-hidden />
        </Link>
      </TooltipTrigger>
      <TooltipContent side={side}>Settings</TooltipContent>
    </Tooltip>
  )
}
